/* eslint-disable no-prototype-builtins */
import { AccountField } from 'components/shared/dropdowns'
import {
  AccountType,
  CreateAccountMutationVariables,
  EmailExistsDocument,
  EmailExistsQuery,
  EmailExistsQueryVariables,
  useClientTypesQuery,
  useCreateAccountMutation,
} from 'graphql/bindings/AdminBindings'
import { AdminClient } from 'graphql/clients'
import {
  Box,
  FlagField,
  FormModal,
  FormSectionTitle,
  SpinnerButton,
  TextField,
  ValueAutocompleteField,
  useRouter,
} from '@forgeglobal/design-framework'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { InvestorShareholderOpts, accountTypeOptions } from 'util/Enums'
import { MutationResult } from 'react-apollo'
import { Routes } from 'util/Routes'
import { countryOptions, usStateOptions } from 'util/LocationUtils'
import React, { Fragment, useEffect } from 'react'

const required = (value: any, allValues: any) => (allValues.visitAccount || value ? undefined : 'Required')

const emailExistsQuery = (email: string) =>
  AdminClient.query<EmailExistsQuery, EmailExistsQueryVariables>({
    query: EmailExistsDocument,
    variables: { email },
    fetchPolicy: 'network-only',
  }).then(result => result.data.emailExists)

type FormShape = {
  name?: { label: string; value: string } | string
  type?: AccountType
  investorShareholder: string[]
  client_type_id?: string
  country?: string
  state?: string
  city?: string
  company_id?: number
  create_platform_user: boolean
  is_account_of_interest: boolean
  account_of_interest_reason?: string
  primary_email?: string
  visitAccount?: string
  mutationResult: MutationResult
}

const CreateAccountForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, form, values, valid }) => {
  const { data } = useClientTypesQuery({ variables: { accountType: values.type } })
  const clientTypes =
    data?.client_types
      ?.map(ctype => ({ label: ctype.name, value: ctype.id }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? []
  const mutateInProgress = values.mutationResult.called
  const formChange = form.change
  const formSubmit = form.submit
  useEffect(() => {
    if (values.name && values.name.hasOwnProperty('label')) {
      formChange('visitAccount', (values.name as { label: string; value: string }).value)
      formSubmit()
    }
  }, [formChange, formSubmit, values.name])
  return (
    <form onSubmit={handleSubmit}>
      <FormSectionTitle>Account Details</FormSectionTitle>
      <AccountField name="name" label="Name (required)" validate={required} freeSolo />
      <ValueAutocompleteField name="type" label="Type (required)" options={accountTypeOptions} />
      <ValueAutocompleteField
        name="investorShareholder"
        label="Investor/Shareholder"
        options={InvestorShareholderOpts}
        multiple
      />
      <ValueAutocompleteField name="client_type_id" label="Client Type" options={clientTypes} />
      {values.type === 'individual' && (
        <Fragment>
          <Field
            name="primary_email"
            label="Primary Email"
            component={TextField}
            validate={(email, values: any) =>
              email &&
              values.create_platform_user &&
              emailExistsQuery(email).then(exists => (exists ? 'Email already taken' : undefined))
            }
            fullWidth
          />
          <Field
            name="create_platform_user"
            label="Create user in Admin for this account"
            component={FlagField}
            disabled={!values.primary_email}
            attached
            fullWidth
          />
        </Fragment>
      )}

      <FormSectionTitle>Location</FormSectionTitle>
      <ValueAutocompleteField name="country" label="Country" options={countryOptions} />
      {values.country === 'US' && <ValueAutocompleteField name="state" label="State" options={usStateOptions} />}
      {values.country && <Field name="city" component={TextField} label="City" fullWidth />}
      <FormSectionTitle>Account of Interest</FormSectionTitle>
      <Field name="is_account_of_interest" label="Flag as an Account of Interest?" component={FlagField} fullWidth />
      {values.is_account_of_interest && (
        <Field name="account_of_interest_reason" label="Reason for AOI" component={TextField} fullWidth />
      )}
      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          disabled={!valid}
          loading={mutateInProgress}
          caption="Save"
        />
      </Box>
    </form>
  )
}

interface CreateAccountModalProps {
  hideModal: () => void
  accountSuggestionId?: string
  email?: string
  name?: string
}

export const CreateAccountModal: React.FC<CreateAccountModalProps> = props => {
  const { history } = useRouter()
  const [mutate, mutateResult] = useCreateAccountMutation({
    refetchQueries: ['accountSuggestions'],
    onCompleted: data => {
      props.hideModal()
      history.push(Routes.account(data.createAccount.id))
    },
  })

  const onSubmit = (values: FormShape) => {
    if (values.visitAccount) {
      history.push(Routes.account(values.visitAccount))
      props.hideModal()
      return
    }

    const isInvestor: boolean = values.investorShareholder.includes('Investor')
    const isShareholder: boolean = values.investorShareholder.includes('Shareholder')

    const {
      type,
      name,
      client_type_id,
      create_platform_user,
      is_account_of_interest,
      account_of_interest_reason,
      primary_email,
      country,
      state,
      city,
      company_id,
    } = values

    if (!type || !name) return

    const variables: CreateAccountMutationVariables = {
      form: {
        type,
        name: name as string,
        client_type_id,
        country,
        state,
        city,
        create_platform_user,
        is_account_of_interest,
        account_of_interest_reason,
        company_id,
        primary_email,
        is_investor: isInvestor,
        is_shareholder: isShareholder,
        account_suggestion_id: props.accountSuggestionId,
      },
    }
    mutate({ variables })
  }

  const initialValues: FormShape = {
    investorShareholder: [],
    type: AccountType.Individual,
    create_platform_user: false,
    is_account_of_interest: false,
    name: props.name ? { label: props.name, value: props.name } : undefined,
    primary_email: props.email,
    mutationResult: mutateResult,
  }

  return (
    <FormModal open hideModal={props.hideModal} title="Create: Account">
      <Form component={CreateAccountForm} onSubmit={onSubmit} initialValues={initialValues} />
    </FormModal>
  )
}
